import type { Platform } from '../platform/types';
import { storeImage } from '../db/images';
import { sniffImageMime } from './mime';

/**
 * **This is the whole point of the feature.** Every chosen image is downloaded
 * and stored as a Blob in Dexie exactly like a manual upload — original plus
 * thumbnail, intrinsic size measured. Nothing is kept as a remote URL.
 *
 * If the retailer deletes the listing tomorrow, the item is untouched. A
 * remote URL in an item's image list is a bug (invariant 3).
 */

export type Crystallised = {
  /** Stored image ids, in the order the page offered them. */
  imageIds: string[];
  /** How many were offered but couldn't be saved. Reported, never fatal. */
  failed: number;
};

export async function crystallise(
  platform: Platform,
  urls: readonly string[],
  /** The product page. Retail CDNs 403 image requests without one. */
  referer: string,
  onProgress: (done: number, total: number) => void,
): Promise<Crystallised> {
  const imageIds: string[] = [];
  let failed = 0;

  onProgress(0, urls.length);

  // Sequential, like every other image path in the app: a dozen simultaneous
  // `createImageBitmap` calls on full-size product photography stalls the
  // frame, and downloading in parallel from one host invites a rate limit.
  for (const url of urls) {
    try {
      imageIds.push(await save(platform, url, referer));
    } catch {
      // One 403 among twelve shouldn't lose the other eleven.
      failed += 1;
    }

    onProgress(imageIds.length + failed, urls.length);
  }

  return { imageIds, failed };
}

async function save(platform: Platform, url: string, referer: string): Promise<string> {
  const bytes = await platform.fetchBinary(url, referer);

  const mime = await sniffImageMime(bytes);
  if (mime === undefined) {
    // Not an image at all — usually a bot-check page served with a 200.
    throw new Error(`${url} didn't return an image`);
  }

  return storeImage(new Blob([bytes], { type: mime }));
}
